import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import { addCompany } from '../../slices/companies/companiesSlice';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { toast } from 'react-toastify';
import { ProgressOverlay } from '../progress-overlay/ProgressOverlay';
import { FormControl, FormHelperText } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { NWClient } from '../../client/NWClient';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import { selectCountriesInfo } from '../../slices/countries/countriesSlice';
import { Company } from '../../models/company.model';
import { TextValidator, ValidatorForm } from '../form-validator';
import Tooltip from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/Info';
import { useErrorMessage } from '../hooks/useErrorMessage';
//import WhatsAppOptInCheckbox from '../whatsapp-opt-in-checkbox/WhatsAppOptInCheckbox';
import { parsePhoneNumberFromString } from 'libphonenumber-js/max';
import PhoneNumberInput from '../form-inputs/phone-number-input/PhoneNumberInput';

const CompanyCreateForm = () => {
  const { t } = useTranslation();
  const { getErrorMessage } = useErrorMessage();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [businessName, setBusinessName] = useState<string>('');
  const [country, setCountry] = useState<{ value: string; title: string } | null>(null);
  const [businessPhoneNumber, setBusinessPhoneNumber] = useState<string>('');
  //const [whatsappOptIn, setWhatsappOptIn] = useState(false);
  const [businessRegistrationNumber, setBusinessRegistrationNumber] = useState<string>('');
  const [fieldsErrors, setFieldsErrors] = useState<Record<string, string[]> | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const countries = useAppSelector(selectCountriesInfo);
  const countriesOptions = [...countries]
    .filter((el) => el.code !== 'GB')
    .map((el) => ({ value: el.name, title: el.name }));
  const countriesLoading = useAppSelector((state) => state.countriesInfo.status);
  const companyLoadingStatus = useAppSelector((state) => state.companies.status);
  const onlyDigitSpaceRegExp = useMemo(() => {
    return /^[0-9 ]*$/;
  }, []);
  // Optional leading '+', then any number of digits (no length limit).
  const phoneRegExp = useMemo(() => {
    return /^\+?\d*$/;
  }, []);
  const businessNameRegEx = useMemo(() => {
    return /^[a-zA-Z0-9 &\-]*$/;
  }, []);

  const handleInputsPossibleErrors = (propertyName: string) => {
    if (fieldsErrors && fieldsErrors[propertyName]) {
      const obj = { ...fieldsErrors };
      delete obj[propertyName];
      setFieldsErrors(obj);
    }
  };

  const handleBusinessRegistrationNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    setBusinessRegistrationNumber(e.target.value);
    handleInputsPossibleErrors('business_abn_acn');
  };

  const handleBusinessNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setBusinessName(e.target.value);
    handleInputsPossibleErrors('name');
  };

  const createCompany = (e: React.SyntheticEvent) => {
    e.preventDefault();

    const parsed = parsePhoneNumberFromString(businessPhoneNumber);

    if (!parsed?.isValid()) {
      setPhoneError(t('messages.phoneInvalid'));
      return;
    }

    /*if (whatsappOptIn) {
      const phoneType = parsed.getType();
      if (phoneType !== 'MOBILE' && phoneType !== 'FIXED_LINE_OR_MOBILE') {
        setPhoneError(t('messages.phoneNotWhatsApp'));
        return;
      }
    }*/

    setLoading(true);
    const data = {
      name: businessName.trim(),
      phone_number: parsed.number,
      business_abn_acn: businessRegistrationNumber.trim().replace(/\s/g, ''),
      country: country ? country.value : undefined,
      postal_country: country ? country.value : undefined,
      physical_country: country ? country.value : undefined,
    };
    dispatch(addCompany({ data }))
      .unwrap()
      .then((res) => {
        const createdCompany = res as Company;
        toast.success(t('messages.companyCreated'));
        const id = Number(createdCompany.id);
        return NWClient.post(
          `business-profile-details/${id}`,
          {
            company: id,
            business_phone_number: parsed.number,
            business_country: country ? country.value : null,
            registration_number: businessRegistrationNumber.trim().replace(/\s/g, ''),
            //marketing_opt_in: whatsappOptIn,
          },
          false
        ).then(() => {
          NWClient.post(
            `business-profile-bank-details/${id}`,
            { company: id, currency: 'AUD' },
            false
          ).then(() => {
            window.location.reload();
          });
        });
      })
      .catch((error) => {
        toast.error(getErrorMessage(error));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    ValidatorForm.addValidationRule('ABNorACNLength', (value) => {
      return onlyDigitSpaceRegExp.test((value as string).trim());
    });
    return () => {
      ValidatorForm.removeValidationRule('ABNorACNLength');
    };
  }, [onlyDigitSpaceRegExp]);

  useEffect(() => {
    ValidatorForm.addValidationRule('phone', (value) => {
      // Accepts any number of digits with an optional leading '+'.
      // Spaces are tolerated while typing because they are stripped on submit.
      const normalized = String(value ?? '')
        .trim()
        .replace(/\s/g, '');
      return normalized === '' || phoneRegExp.test(normalized);
    });

    return () => {
      ValidatorForm.removeValidationRule('phone');
    };
  }, [phoneRegExp]);

  useEffect(() => {
    ValidatorForm.addValidationRule('valueLength255', (value) => {
      return (value as string).trim().length < 256;
    });

    return () => {
      ValidatorForm.removeValidationRule('valueLength255');
    };
  }, []);

  useEffect(() => {
    ValidatorForm.addValidationRule('businessNameRule', (value) => {
      return businessNameRegEx.test((value as string).trim());
    });

    return () => {
      ValidatorForm.removeValidationRule('businessNameRule');
    };
  }, [businessNameRegEx]);

  return (
    <>
      {countriesLoading === 'loading' || companyLoadingStatus === 'loading' ? (
        <ProgressOverlay position='absolute' />
      ) : (
        <>
          <Box className='company-create-form'>
            <Box className='message-container'>
              <p>{t('messages.finishCompanySetUp')}</p>
            </Box>
            <ValidatorForm onSubmit={createCompany}>
              <div>
                <TextValidator
                  label={t('labels.businessName')}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handleBusinessNameChange(e)}
                  variant='outlined'
                  name='businessName'
                  value={businessName}
                  required={countriesOptions.length > 0}
                  autoComplete='off'
                  validators={['required', 'valueLength255', 'businessNameRule']}
                  errorMessages={[
                    t('messages.fieldRequired'),
                    t('messages.maxLength255'),
                    t('messages.businessNameValidationMessage'),
                  ]}
                />
                {Boolean(fieldsErrors?.name) && (
                  <FormHelperText error={true}>{fieldsErrors?.name.join('.')}</FormHelperText>
                )}
              </div>
              <FormControl>
                <Autocomplete
                  id='country'
                  openOnFocus={true}
                  options={countriesOptions}
                  isOptionEqualToValue={(option, value) => option.title === value.title}
                  getOptionLabel={(option) => option.title}
                  value={country}
                  onChange={(
                    _event,
                    newValue: {
                      title: string;
                      value: string;
                    } | null
                  ) => {
                    setCountry(newValue);
                  }}
                  renderOption={(props, option) => {
                    const { key, ...optionProps } = props;
                    return (
                      <li key={key ?? option.value} {...optionProps}>
                        {option.title}
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required={countriesOptions.length > 0}
                      variant='standard'
                      label={t('labels.country')}
                      disabled={countriesOptions.length === 0}
                    />
                  )}
                  disabled={countriesOptions.length === 0}
                />
              </FormControl>
              <div>
                <PhoneNumberInput
                  value={businessPhoneNumber}
                  onChange={(v) => {
                    setBusinessPhoneNumber(v);
                    setPhoneError(null);
                    handleInputsPossibleErrors('phone_number');
                  }}
                  disabled={false}
                  label={t('formInputs.businessPhoneNumberInputLabel') + ' *'}
                  error={Boolean(phoneError)}
                  helperText={phoneError ?? undefined}
                />
                {Boolean(fieldsErrors?.phone_number) && (
                  <FormHelperText error={true}>
                    {fieldsErrors?.phone_number.join('.')}
                  </FormHelperText>
                )}
              </div>
              <div>
                <TextValidator
                  label={
                    <>
                      <Box component='span' sx={{ marginRight: 0.5 }}>
                        {t('labels.ABNorACN')}
                      </Box>
                      <Tooltip
                        title={t('labels.registrationNumberTypes')}
                        placement='top'
                        arrow={true}
                      >
                        <InfoIcon
                          fontSize='small'
                          color='secondary'
                          sx={{ flexShrink: 0 }}
                          className={'info-icon'}
                        />
                      </Tooltip>
                    </>
                  }
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    handleBusinessRegistrationNumberChange(e)
                  }
                  name='businessABNorACN'
                  validators={['ABNorACNLength']}
                  value={businessRegistrationNumber}
                  errorMessages={[t('messages.ABNandACNMessage')]}
                  autoComplete='off'
                />
                {Boolean(fieldsErrors?.business_abn_acn) && (
                  <FormHelperText error={true}>
                    {fieldsErrors?.business_abn_acn.join('.')}
                  </FormHelperText>
                )}
              </div>
              {/*<div>
                <WhatsAppOptInCheckbox
                  value={whatsappOptIn}
                  onChange={setWhatsappOptIn}
                  disabled={false}
                />
              </div>*/}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'flex-start',
                  width: '100%',
                  marginTop: 'auto',
                }}
              >
                <Button
                  id='companyCreateFormSubmitBtn'
                  variant='contained'
                  className='btn-default btn-action-basic'
                  type='submit'
                  disabled={
                    !businessName ||
                    (!country && countriesOptions.length > 0) ||
                    !businessPhoneNumber
                  }
                >
                  {t('buttons.submit')}
                </Button>
              </Box>
            </ValidatorForm>
          </Box>
          {loading && <ProgressOverlay />}
        </>
      )}
    </>
  );
};

export default CompanyCreateForm;
